import { format } from "date-fns";

import type { Tour } from "../tours/types";
import type {
  Address,
  DriverResponse,
  SearchToursResponse,
  SettingsResponse,
  TourDetailsResponse,
  TourSummaryResponse,
} from "./types";

import { apiClient } from "./client";

export async function fetchTours(
  date: Date,
  driverId?: string,
): Promise<SearchToursResponse> {
  const response = await apiClient.get("/tours", {
    params: {
      date: format(date, "yyyy-MM-dd"),
      driver: driverId,
      // TODO: Add pagination
      limit: 100,
    },
  });
  return response.data;
}

export async function fetchTourDetails(
  tourId: string,
): Promise<TourDetailsResponse> {
  const response = await apiClient.get(`/tours/${tourId}/summary`);
  return response.data;
}

export async function startTourApi(tourId: string): Promise<Tour> {
  const response = await apiClient.post(`/tours/${tourId}/start`);
  return response.data;
}

export async function endTourApi(id: string): Promise<Tour> {
  const response = await apiClient.post(`/tours/${id}/complete`);
  return response.data;
}

export async function loadOrder(orderId: string) {
  const response = await apiClient.patch(`/orders/${orderId}/load`);
  return response.data;
}

export async function loadOrders(orderIds: string[]) {
  const response = await apiClient.patch(`/orders/load`, {
    orderIds,
  });
  return response.data;
}

export async function getOrder(inputs: { orderId: string; isScan?: boolean }) {
  const params = inputs.isScan ? { scan: inputs.isScan } : undefined;
  const response = await apiClient.get(`/orders/${inputs.orderId}`, { params });
  return response.data;
}

export async function loadGather(gatherTaskId: string) {
  const response = await apiClient.patch(`/gatherTasks/${gatherTaskId}/load`);
  return response.data;
}

export async function dropOffGather(
  gatherTaskId: string,
  inputs: {
    driverNote?: string;
  },
) {
  const response = await apiClient.patch(
    `/gatherTasks/${gatherTaskId}/collected`,
    {
      driverNote: inputs.driverNote,
    },
  );
  return response.data;
}

export async function markGatherFailed(inputs: {
  id: string;
  driverNote: string;
}): Promise<void> {
  await apiClient.patch(`/gatherTasks/${inputs.id}/deliveryFailed`, {
    driverNote: inputs.driverNote,
  });
}

export async function markDeliveryFailed(inputs: {
  id: string;
  abortReason: string;
  driverNote: string;
}) {
  const response = await apiClient.patch(
    `/orders/${inputs.id}/deliveryFailed`,
    {
      abortReason: inputs.abortReason,
      driverNote: inputs.driverNote,
    },
  );
  return response.data;
}

export async function fetchPharmacyNameById(pharmacyId: string): Promise<{
  name: string;
  address: Address;
}> {
  const response = await apiClient.get(`/pharmacies/${pharmacyId}`);
  return response.data;
}

export async function deliverOrder(inputs: {
  id: string;
  paymentCollected?: number;
  signature?: string;
  handedToName?: string;
  handedToAddress?: string;
  driverNote?: string;
  paymentType?: string;
}) {
  const response = await apiClient.patch(`/orders/${inputs.id}/delivered`, {
    paymentCollected: inputs.paymentCollected,
    signature: inputs.signature,
    handedToName: inputs.handedToName,
    handedToAddress: inputs.handedToAddress,
    driverNote: inputs.driverNote,
    paymentType: inputs.paymentType,
  });
  return response.data;
}

export async function deliverBulkOrder(inputs: {
  id: string;
  paymentCollected?: number;
  signature?: string;
  handedToName?: string;
  handedToAddress?: string;
  driverNote?: string;
  paymentType?: string;
}) {
  const response = await apiClient.patch(`/bulkOrders/${inputs.id}/delivered`, {
    paymentCollected: inputs.paymentCollected,
    signature: inputs.signature,
    handedToName: inputs.handedToName,
    handedToAddress: inputs.handedToAddress,
    driverNote: inputs.driverNote,
    paymentType: inputs.paymentType,
  });
  return response.data;
}

export async function deliverFailBulkOrder(inputs: {
  id: string;
  driverNote: string;
  abortReason: string;
}): Promise<void> {
  await apiClient.patch(`/bulkOrders/${inputs.id}/deliveryFailed`, {
    driverNote: inputs.driverNote,
    abortReason: inputs.abortReason,
  });
}

export function uploadDeliveryProof(inputs: {
  orderId: string;
  tourTaskId: string;
  base64: string;
}) {
  return apiClient.post(
    `/orders/${inputs.orderId}/uploadPoDB64/${inputs.tourTaskId}`,
    {
      image: inputs.base64,
    },
  );
}

export function fetchPaymentSummary(tourId: string) {
  return apiClient.get<TourSummaryResponse>(`/tours/${tourId}/paymentSummary`);
}

export async function fetchDriver() {
  const response = await apiClient.get<DriverResponse>("/drivers/self");
  return response.data;
}

export async function fetchOrganizationSettings() {
  const response = await apiClient.get<SettingsResponse>(
    "/organizations/settings",
  );
  return response.data;
}

export async function fetchChats(inputs: { limit?: number; offset?: number }) {
  const response = await apiClient.get("/chats", {
    params: {
      limit: inputs.limit,
      offset: inputs.offset,
    },
  });
  return response.data;
}

export async function fetchChat(inputs: {
  conversationId: string;
  limit?: number;
  offset?: number;
}) {
  const response = await apiClient.get(
    `/chats/${inputs.conversationId}/messages`,
    {
      params: {
        limit: inputs.limit,
        offset: inputs.offset,
      },
    },
  );
  return response.data;
}

export async function fetchUploadFileUrl(inputs: {
  conversationId: string;
  file: File;
}) {
  const formData = new FormData();
  formData.append("file", inputs.file);

  const response = await apiClient.post<{ url: string; fileId: string }>(
    `/chats/${inputs.conversationId}/upload`,
    formData,
  );
  return response.data;
}
